import type { AgentTokenScope } from 'definitions/agent-tokens';

/** What each scope lets an agent do, in the order the form lists them. */
export const SCOPE_COPY: Record<AgentTokenScope, { label: string; short: string; description: string }> = {
  read: {
    label: 'Read',
    short: 'Read',
    description: 'Experiments, results, surveys, learnings and website settings.',
  },
  'experiments:write': {
    label: 'Build experiments',
    short: 'Build',
    description:
      'Create and edit Draft and QA experiments with their variants and weights, and write the debrief of an ended one. Running experiments stay as they are.',
  },
  'experiments:lifecycle': {
    label: 'Start and stop experiments',
    short: 'Run',
    description: 'Start, end, archive and schedule experiments.',
  },
  'experiments:deploy': {
    label: 'Deploy winners',
    short: 'Deploy',
    description: 'Deploy a winning variant to every visitor.',
  },
  'surveys:write': {
    label: 'Build surveys',
    short: 'Surveys',
    description:
      'Create surveys, and edit draft surveys with their questions and targeting. Running surveys stay as they are.',
  },
  'surveys:lifecycle': {
    label: 'Start and stop surveys',
    short: 'Run surveys',
    description: 'Start, end and archive surveys.',
  },
  'websites:publish': {
    label: 'Publish websites',
    short: 'Publish',
    description: "Rebuild and publish a website's experiment script.",
  },
};

/** No em or en dashes anywhere here (UI copy rule). */
export const AGENT_ACCESS_COPY = {
  newToken: 'New token',
  createTitle: 'New token',
  createDescription:
    'A token lets an AI agent, such as Claude through the Pertento connector, work in Pertento as you. It can only do what you can, narrowed to the scopes and websites you choose.',
  nameLabel: 'Name',
  namePlaceholder: 'Claude Code, laptop',
  nameHelp: 'Where the token will be used, so you can tell tokens apart later.',
  scopesLabel: 'What it may do',
  memberScopes: 'Your role can read but not change anything, so your tokens are read only.',
  websitesLabel: 'Websites',
  allWebsites: 'Every website you work on',
  staffAllWebsites: 'Staff tokens that can make changes must name the sites they reach, so pick them below.',
  someWebsites: 'Only the websites I pick',
  noWebsitesYet: 'Pick at least one website.',
  selectAll: 'Select all',
  selectedCount: (selected: number, total: number) => `${selected} of ${total} selected`,
  tooManyWebsites: (max: number) => `A token can name at most ${max} websites.`,
  moreWebsites: (count: number) => `and ${count} more`,
  expiryLabel: 'Expires after',
  passwordLabel: 'Your password',
  passwordHelp: 'A token keeps working after you sign out, so making one asks for your password again.',
  needPassword: 'Enter your password',
  expiryOption: (days: number) => (days === 365 ? '1 year' : `${days} days`),
  create: 'Create token',
  creating: 'Creating',
  needName: 'Enter a name',
  needScope: 'Choose at least one scope',
  cancel: 'Cancel',
  secretTitle: 'Copy your token',
  tokenLabel: 'Your token',
  copyToken: 'Copy the token',
  commandLabel: 'Add it to Claude Code',
  copyCommand: 'Copy the command',
  closeTitle: 'Close without copying?',
  closeBody:
    'This token cannot be shown again. If you have not saved it, you will need to revoke it and make a new one.',
  closeConfirm: 'Close anyway',
  secretOnce:
    'Copy it now: it is shown only once, and Pertento keeps no copy. Anyone who has it can act as you within its scopes.',
  copy: 'Copy',
  copied: 'Copied',
  copyFailed: 'Could not copy. Select the token and copy it by hand.',
  dismissSecret: 'Done',
  createFailed: 'Could not create the token.',
  optionsFailed: 'Could not load the token options.',
  passkeyCancelled: 'Passkey confirmation was cancelled. Try again.',
  passkeyFailed: 'Your passkey could not be confirmed. Try again.',
  passkeyElsewhere: (places: readonly string[]) =>
    `Your passkey is registered on ${places.join(' and ')}. Create the token there, or add a passkey on this site.`,
  passkeyElsewhereShort: 'Create the token where your passkey is registered, or add a passkey on this site.',
  managePasskeys: 'Manage passkeys',
  extensionPlace: 'the Pertento Chrome extension',
  tokensTitle: 'Your tokens',
  tokensDescription: 'Revoke a token you no longer use, or one you think someone else has. It stops working at once.',
  teamTitle: "Your team's tokens",
  teamDescription: 'Every token made by someone in this organization. Revoke one to stop that agent at once.',
  empty:
    'No tokens yet. A token lets an AI agent, such as Claude, read your results or build experiments as you, with only the scopes and websites you choose.',
  teamEmpty: 'Nobody in this organization has made a token.',
  loadFailed: 'Could not load the tokens.',
  retry: 'Retry',
  columns: {
    name: 'Name',
    owner: 'Made by',
    prefix: 'Token',
    lastUsed: 'Last used',
    expires: 'Expires',
    status: 'Status',
    access: 'Access',
    actions: 'Actions',
  },
  tableLabel: 'Agent tokens',
  teamTableLabel: "Your team's agent tokens",
  allWebsitesShort: 'All websites',
  accessDetails: 'What can this token do:',
  websiteCount: (count: number) => (count === 1 ? '1 website' : `${count} websites`),
  companyCount: (count: number) => (count === 1 ? '1 account' : `${count} accounts`),
  never: 'Never',
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
  revoke: 'Revoke',
  revokeTitle: (name: string) => `Revoke "${name}"?`,
  revokeBody:
    'Any agent using this token loses access on its next request. This cannot be undone; make a new token if you need one again.',
  revokedToast: (name: string) => `Revoked "${name}"`,
  revokeFailed: 'Could not revoke the token.',
  // Connected apps (PER-334): OAuth connections, such as Claude or ChatGPT added as a connector.
  appsTitle: 'Connected apps',
  appsDescription:
    'Apps you allowed to work in Pertento as you, such as Claude or ChatGPT added as a connector. Disconnect one and it stops working at once.',
  appsEmpty: 'No apps connected. Add Pertento as a connector in Claude or ChatGPT and sign in to connect one.',
  appsTableLabel: 'Connected apps',
  disconnect: 'Disconnect',
  disconnectTitle: (name: string) => `Disconnect "${name}"?`,
  disconnectBody:
    'The app loses access on its next request. To use it again, connect it again from the app, which asks you to allow it here.',
  disconnectedToast: (name: string) => `Disconnected "${name}"`,
} as const;
